<template>
  <main class="documents-page-container">
    <DocumentUploadModal
      :show="isDocUploadModalActive"
      :is-modal-active="isDocUploadModalActive"
      :uploaded-docs="uploadedDocs"
      :account="$store.state.account"
      :enable-folder-assignment="isStaffOnly"
      :enable-doc-visibility-assignment="canSeeDocVisibilityAssignment"
      :enableLinksTab="isStaffOnly"
      :evaporate-response="evaporateResponse"
      :s3-bucket="s3Bucket"
      :folders="folders"
      :folder="folder"
      :clearableVisibilityOption="false"
      @search-folder="handleFolderSearch"
      @selected-folder="handleFolderSelection"
      @create-folder="handleFolderSelection"
      @on-close-modal="toggleDocUploadModal"
      @on-file-upload-complete="addUploadedDocToState($event)"
      @on-submit="saveDocumentUpload"
    />
    <div class="hero-container">
      <div class="header-text-column">
        <div class="header-text">
          <h1>Explore {{ allDocumentsCount }} public records</h1>
          <NrIconButton
            aria-label="Toggle footer and subheader text"
            variant="minimal-neutral"
            @click="setHeroOpen(!heroOpen)"
            :icon="{
              name: heroOpen ? 'chevron-down' : 'chevron-up',
              type: 'regular'
            }"
          />
        </div>
        <p v-show="heroOpen">
          Use filters to find documents you're looking for.
        </p>
      </div>
      <div :class="{ 'document-button-flex': !heroOpen }">
        <div class="document-button-column">
          <div class="document-button-container">
            <NrButton
              variant="bordered-neutral"
              v-if="$can('manage', 'Document')"
              @click="toggleDocUploadModal"
              aria-label="Upload documents"
            >
              Upload documents
            </NrButton>
            <div v-if="showDeleteAllDocuments" class="document-delete-button">
              <NrButton
                variant="filled-danger"
                aria-label="Delete all documents"
                @click="toggleDeleteAllDocsModal"
                >Delete all documents
              </NrButton>
              <NrModal
                primaryButtonText="Delete"
                secondaryButtonText="Cancel"
                title="Delete all documents"
                hasSecondaryButton
                :show="isDeleteAllDocsModalActive"
                @on-primary-click="
                  deleteAllDocuments(), toggleDeleteAllDocsModal()
                "
                @on-secondary-click="toggleDeleteAllDocsModal"
              >
                <template>
                  {{ deleteAllDocsModalBody }}
                </template>
              </NrModal>
            </div>
          </div>
        </div>
        <div :class="{ 'pagination-wrapper': heroOpen }">
          <NrPagination
            :current="pageNum"
            aria-next-label="Next page"
            aria-previous-label="Previous page"
            aria-page-label="Page"
            aria-current-label="Current page"
            :perPage="perPage"
            :total="paginationCount"
            @change="setTablePage"
          />
        </div>
      </div>
    </div>
    <div class="documents-body has-background-white-bis">
      <div v-show="showFilter" class="documents-sidefilter">
        <span class="sidefilter">
          <div class="aside-header level is-mobile is-size-5 is-marginless">
            <div class="level-left">
              <div class="level-item has-text-weight-semibold">Filters</div>
              <div class="level-item">
                <NrButton
                  class="reset-filter-button"
                  @click="resetQueryFilters"
                  variant="minimal-neutral"
                  aria-label="Reset Filters"
                >
                  Reset filters
                </NrButton>
              </div>
            </div>
          </div>

          <div
            :class="[
              'aside-content',
              'has-background-white-bis',
              { 'documents-aside-large': !isSmallScreen() }
            ]"
          >
            <div>
              <NrFormInput
                class="search-filter"
                v-model="searchFilter"
                placeholder="Enter keyword"
                aria-label="Type here to filter by document name"
                label="Search file names"
                @input="setSearchTerm(searchFilter)"
              />
              <NrSelect
                v-if="$can('manage', 'Document')"
                placeholder="Search and select a folder"
                :modelValue="folderFilter"
                name="folder_name"
                :options="folders"
                :hasMore="!folderFilterState.end"
                :loading="folderFilterState.fetching"
                @load-more="handleFolderScroll"
                @open="handleFolderSearch"
                @search="handleFolderSearch"
                @input="handleFolderInput"
                :filterable="false"
                appendToBody
              >
                <template v-slot:label>Filter by folder</template>
              </NrSelect>

              <NrDatepicker
                v-model="uploadDatesFilter.start"
                :maxDate="uploadDatesFilter.end"
                @update:modelValue="
                  setUploadDateRange({
                    position: 'start',
                    date: uploadDatesFilter.start
                  })
                "
              >
                <template v-slot:label>Upload date (start)</template>
                <template v-slot:trigger>
                  <NrIcon name="calendar-alt" />
                </template>
              </NrDatepicker>

              <NrDatepicker
                v-model="uploadDatesFilter.end"
                :minDate="uploadDatesFilter.start"
                @update:modelValue="
                  setUploadDateRange({
                    position: 'end',
                    date: uploadDatesFilter.end
                  })
                "
              >
                <template v-slot:label>Upload date (end)</template>
                <template v-slot:trigger>
                  <NrIcon name="calendar-alt" />
                </template>
              </NrDatepicker>

              <NrDatepicker
                v-model="documentDatesFilter.start"
                :minDate="documentDatesFilter.end"
                @update:modelValue="
                  setDocumentDateRange({
                    position: 'start',
                    date: documentDatesFilter.start
                  })
                "
              >
                <template v-slot:label>Document date (start)</template>
                <template v-slot:trigger>
                  <NrIcon name="calendar-alt" />
                </template>
              </NrDatepicker>

              <NrDatepicker
                v-model="documentDatesFilter.end"
                :minDate="documentDatesFilter.start"
                @update:modelValue="
                  setDocumentDateRange({
                    position: 'end',
                    date: documentDatesFilter.end
                  })
                "
              >
                <template v-slot:label>Document date (end)</template>
                <template v-slot:trigger>
                  <NrIcon name="calendar-alt" />
                </template>
              </NrDatepicker>

              <div class="filters-submit">
                <NrButton
                  class="m-right-12 is-hidden-desktop"
                  variant="bordered-primary"
                  aria-label="Cancel"
                  @click="toggleFilter"
                >
                  Cancel
                </NrButton>
                <NrTooltip label="ctrl+enter to apply filters">
                  <NrButton aria-label="Apply Filters" @click="fetchDocuments">
                    Apply filters
                  </NrButton>
                </NrTooltip>
              </div>
            </div>
          </div>
        </span>
      </div>
      <div class="table-container" id="skip-to-main-content">
        <DocumentsTable :height="tableHeight" @sort="setTableSort" />
      </div>
    </div>
    <Footer
      :account="$store.state.account"
      :useStaffHelpLink="canReadStaffOnly"
    />
  </main>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DocumentUploadModal from '@nextrequestco/components/src/components/DocumentUploadModal/DocumentUploadModal.vue'
import Footer from '../../../vue_shared/components/Footer.vue'
import setupEvaporateFileUploader from '../../../mixins/init_evaporate'
import setupApplyFilterKeyEventListeners from '../../../mixins/setupApplyFilterKeyEventListeners'
import removeApplyFilterKeyEventListeners from '../../../mixins/removeApplyFilterKeyEventListeners'
import togglePageScroll from '../../../mixins/toggle-page-scroll.ts'
import { debounce } from 'lodash'
import NrDatepicker from '@nextrequestco/components/src/components/NrDatePicker/nr-datepicker.vue'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton/nr-icon-button.vue'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import NrButton from '@nextrequestco/components/src/components/NrButton/nr-button'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip.vue'
import NrPagination from '@nextrequestco/components/src/components/NrPagination/nr-pagination.vue'
import DocumentsTable from './DocumentsTable.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'

export default {
  data() {
    return {
      filterFolders: [],
      folder: null,
      windowHeight: 500,
      tableHeight: 300,
      containerHeight: 300,
      sidefilterHeight: 300,
      isDocUploadModalActive: false,
      isDeleteAllDocsModalActive: false,
      deleteAllDocsModalBody:
        'You are about to delete ALL documents from your portal. This will delete every document, including ' +
        'all sample documents and any documents you created.',
      uploadDatesFilter: { start: '', end: '' },
      documentDatesFilter: { start: '', end: '' },
      searchFilter: '',
      searchFolderTerm: '',
      evaporateResponse: {},
      s3Bucket: '',
      keysPressed: {},
      applyFilterEventListener: () => {},
      resetKeysEventListener: () => {}
    }
  },

  mixins: [
    setupApplyFilterKeyEventListeners,
    removeApplyFilterKeyEventListeners,
    setupEvaporateFileUploader,
    togglePageScroll
  ],

  components: {
    DocumentsTable,
    DocumentUploadModal,
    NrModal,
    Footer,
    NrDatepicker,
    NrIconButton,
    NrIcon,
    NrButton,
    NrFormInput,
    NrSelect,
    NrTooltip,
    NrPagination
  },
  created() {
    this.setupApplyFilterKeyEventListeners()
    window.addEventListener('resize', this.getHeights)
    this.getHeights()
  },
  unmounted() {
    this.removeApplyFilterKeyEventListeners()
    window.removeEventListener('resize', this.getHeights)
  },
  watch: {
    // Reset date range and search fields when reset filter button is clicked
    uploadDateRange() {
      if (this.uploadDateIsEmpty) {
        this.uploadDatesFilter = { start: '', end: '' }
      }
    },

    documentDateRange() {
      if (this.documentDateIsEmpty) {
        this.documentDatesFilter = { start: '', end: '' }
      }
    },

    searchTerm() {
      if (this.searchTermIsEmpty) {
        this.searchFilter = ''
      }
    },

    heroOpen() {
      this.getHeights()
    },

    isDocUploadModalActive(next, prev) {
      if (!next || next === prev) {
        return
      }

      this.fetchFolders()
    }
  },

  computed: {
    ...mapGetters('documents', [
      'allDocumentsCount',
      'currentPageNumber',
      'columns',
      'documentDateRange',
      'documents',
      'folderFilter',
      'perPage',
      'searchTerm',
      'searchResultsCount',
      'sortOrder',
      'sortField',
      'isLoading',
      'uploadDateRange',
      'uploadedDocs',
      'showFilter',
      'heroOpen',
      'windowHeight'
    ]),
    ...mapState('documents', {
      folderFilterState: (state) => state.folderFilterState,
      folders: (state) =>
        // eslint-disable-next-line camelcase
        state.folders.map(({ id, folder_name }) => ({
          value: id,
          label: folder_name // eslint-disable-line camelcase
        }))
    }),
    ...mapState(['account']),

    canReadStaffOnly() {
      return this.$can('read', 'staff_only_items')
    },

    tableColumns() {
      return this.columns.filter((column) => {
        if (column.field === 'folder_name') {
          return this.$can('manage', 'Document')
        } else {
          return !!column
        }
      })
    },

    canSeeDocVisibilityAssignment() {
      return this.$ability.can('read', 'publisher_only_items')
    },
    isStaffOnly() {
      return this.$ability.can('read', 'staff_only_items')
    },

    showDeleteAllDocuments() {
      if (!this.account) return false

      return !!(
        ['onboarding', 'sandbox', 'unconfirmed', 'unverified'].includes(
          this.account.state
        ) &&
        this.documents &&
        this.$ability.can('read', 'staff_only_items')
      )
    },

    uploadDateIsEmpty() {
      return this.uploadDateRange.length === 0
    },

    documentDateIsEmpty() {
      return this.documentDateRange.length === 0
    },

    searchTermIsEmpty() {
      return this.searchTerm === ''
    },

    paginationCount() {
      return this.searchResultsCount > 9950 ? 9950 : this.searchResultsCount
    },

    pageNum: {
      set(pageNum) {
        this.setTablePage(pageNum)
      },

      get() {
        return this.currentPageNumber
      }
    },

    bodyMaxHeight() {
      return this.isSmallScreen()
        ? 'unset'
        : this.windowHeight - (this.heroOpen ? 188 : 155) + 'px'
    },

    asideMaxHeight() {
      return this.isSmallScreen()
        ? 'unset'
        : this.windowHeight - (this.heroOpen ? 284 : 210) + 'px'
    }
  },

  mounted() {
    this.fetchFolderFilter()
    this.setupAllDocsPageData(this.$ability)
    this.setupEvaporateFileUploader()
    this.s3Bucket = process.env.S3_BUCKET
    this.togglePageScroll()
  },

  methods: {
    ...mapActions('documents', [
      'addUploadedDocToState',
      'createFolder',
      'deleteAllDocuments',
      'fetchAccount',
      'fetchDocuments',
      'fetchFolders',
      'resetFilters',
      'resetFolders',
      'resetUploadedDocs',
      'saveDocs',
      'setCurrentPageNumber',
      'setDocSubmitDisabled',
      'setDocumentDateRange',
      'setFolderFilter',
      'setHeroOpen',
      'setSearchTerm',
      'setSortField',
      'setSortOrder',
      'setUploadDateRange',
      'setupAllDocsPageData',
      'toggleFilter'
    ]),
    handleFolderScroll: debounce(function () {
      this.handleFolderSearch(this.folderFilterState.search_text)
    }, 250),

    resetQueryFilters() {
      this.uploadDatesFilter = { start: '', end: '' }
      this.documentDatesFilter = { start: '', end: '' }
      if (this.$ability.can('read', 'publisher_only_items')) {
        this.handleRemoveFolderSelection()
      }
      this.resetFilters()
    },

    saveDocumentUpload(payload) {
      this.saveDocs(payload).finally(() => {
        this.folder = null
      })
    },

    handleFolderInput(val) {
      const folder = val || {}
      const folderName = (folder && folder.label) || ''

      this.searchFolderTerm = folderName
      this.setFolderFilter(folderName)
    },

    handleFolderSearch: debounce(async function (name = '') {
      this.folderFilterState.include_empty = false
      this.fetchFolders(name)
    }, 500),

    handleRemoveFolderSelection() {
      this.resetFolders()
      this.filterFolders = []
      this.handleFolderSearch()
      this.setFolderFilter('')
      this.searchFolderTerm = ''
    },

    handleFolderSelection(folder) {
      // Removing the folder association to this document pre-submission
      if (!folder) {
        this.folder = null
        return
      }

      // This indicates that we are creating a new folder
      if (typeof folder === 'string') {
        this.folder = 'pending'

        this.createFolder(folder)
          .then((folder) => {
            this.folder = { value: folder.id, label: folder.folder_name }
          })
          .catch(() => {
            this.folder = null
          })

        return
      }

      // Associate a folder to this document
      this.folder = folder
    },

    getHeights() {
      if (this.heroOpen) {
        this.tableHeight = window.innerHeight - 332
        this.windowHeight = window.innerHeight - 50
        this.sidefilterHeight = window.innerHeight - 457
      } else {
        this.tableHeight = window.innerHeight - 272
        this.windowHeight = window.innerHeight - 1
        this.sidefilterHeight = window.innerHeight - 337
      }
    },

    toggleDocUploadModal() {
      this.resetUploadedDocs()
      this.isDocUploadModalActive = !this.isDocUploadModalActive
      this.folder = null
    },

    toggleDeleteAllDocsModal() {
      this.isDeleteAllDocsModalActive = !this.isDeleteAllDocsModalActive
    },

    setTableSort(sort) {
      this.customSortFieldForPrettyId(sort)
      this.setSortField(sort.field)
      this.setSortOrder(sort.order)
      this.fetchDocuments()
    },

    setTablePage(pageNum) {
      this.setCurrentPageNumber(pageNum)
      this.fetchDocuments()
    },

    customSortFieldForPrettyId(sort) {
      if (sort.field === 'pretty_id') {
        sort.field = 'request_id' // eslint-disable-line no-param-reassign
      }
      return sort
    },

    fetchFolderFilter() {
      const queryTerm = this.$route.query.folder_filter
      if (!queryTerm) return

      this.setFolderFilter(queryTerm)
    },

    applyFilterShortcut(e) {
      this.keysPressed[e.key] = true
      this.triggerApplyFilterShortcut(e)
    },

    triggerApplyFilterShortcut(e) {
      if (
        this.keysPressed.Control &&
        document.activeElement.id !== 'document-search-field'
      ) {
        document.activeElement.blur() // Removes focus on the active element to avoid unchecking a filter when pressing ctrl+enter
      }
      if (this.keysPressed.Control && e.key === 'Enter') {
        this.pageNum = 1
        this.fetchDocuments()
      }
    },

    resetKeysPressed(e) {
      delete this.keysPressed[e.key]
    }
  }
}
</script>

<style scoped lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.documents-page-container {
  display: flex;
  flex-flow: column;
  min-width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  .hero-container {
    display: flex;
    flex-wrap: wrap;
    margin-left: $spacing-12;
    margin-right: $spacing-12;
    margin-top: $spacing-20;
    margin-bottom: $spacing-20;
    .header-text-column {
      margin-bottom: $spacing-4;
      margin-right: auto;
      .header-text {
        display: flex;
        h1 {
          font-weight: $font-weight-semibold;
          font-size: $font-size-5;
        }
        .nr-button {
          margin-left: $spacing-8;
        }
      }
    }
    .document-button-flex {
      display: flex;
      flex-wrap: wrap;
      .document-button-column {
        margin-right: $spacing-16;
      }
    }
    .document-button-column {
      margin-bottom: $spacing-8;
      .document-button-container {
        display: flex;
        flex-direction: row-reverse;
        @media (max-width: 515px) {
          display: block;
        }
        &.document-button-wrap {
          flex-wrap: wrap;
        }
        .document-delete-button {
          margin-right: $spacing-16;
          @media (max-width: 515px) {
            margin-top: $spacing-8;
          }
        }
      }
    }
    .pagination-wrapper {
      display: flex;
      flex-direction: row-reverse;
      @media (max-width: 515px) {
        margin-top: unset;
        flex-direction: row;
      }
      margin-top: $spacing-24;
    }
  }
  .documents-body {
    display: flex;
    @media (max-width: 512px) {
      flex-wrap: wrap;
    }
    max-height: v-bind(bodyMaxHeight);
    .documents-sidefilter {
      min-width: 375px;
      .aside {
        &-header {
          min-height: 40px;
          padding-left: $spacing-12;
        }
        &-content {
          padding: $spacing-12;
          .search-filter {
            margin-bottom: $spacing-8;
          }
        }
      }
      @media (max-width: 512px) {
        flex-grow: 2;
      }
      .filters-submit {
        text-align: end;
      }
    }
    .table-container {
      flex-grow: 2;
    }
  }
  .footer {
    position: absolute;
    bottom: 0;
    width: 100vw;
  }
}
.documents-aside-large {
  height: 100%;
  max-height: v-bind(asideMaxHeight);
  overflow: auto;
}
</style>
